import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { OpenApiRequestStatus, useOpenApiRequest } from "typescript-request-utils";

import { Application } from "../../../../api/models/Application";
import ApplicationModal from "../../../../framework/components/modal/ApplicationModal";
import { getApiRegistry } from "../../../../framework/state/apiRegistry";
import { ViewType } from "../../../../page/enums/ViewType";
import { ApplicationStatus, applicationStatusToString } from "../../../../api/enums/ApplicationStatus";
import { Column, ColumnItem, HeaderColumn, IconColumnItem, ItemWrapper } from "../mobileComponents";
import { MobileItemAcceptIcon, MobileItemClosedIcon, MobileItemDeclineIcon, MobileItemDeleteIcon, MobileItemOpenedIcon } from "../../table/rowIcons";
import { receivedApplicationsTableColumns, sentApplicationsTableColumns } from "../../../configuration/tableColumns";
import useCustomTranslation from "../../../../features/hooks/CustomUseTranslation";
import { useTheme } from "../../../../features/context/CustomerContext";

const StatusColumn = styled(ColumnItem)`
    color: ${props => props.theme.status === ApplicationStatus.ACCEPTED ? "#3D61C1" : props.theme.status === ApplicationStatus.DECLINED ? "#FF5555" : "black"};
`

export interface ApplicationMobileItemProps {
    application: Application;
    type: ViewType;
    onUpdate: () => void;
}

const ApplicationMobileItem: FC<ApplicationMobileItemProps> = (props) => {
    const { t } = useCustomTranslation();
    const theme = useTheme();

    const [showModal, setShowModal] = useState<boolean>(false);
    const [showFullItem, setShowFullItem] = useState<boolean>(false);
    const [modalType, setModalType] = useState<string>('');
    const [modalUpdateError, setModalUpdateError] = useState<boolean>(false);
    const [applicationRequestPending, setApplicationRequestPending] = useState<boolean>(false);

    const [updateApplicationRequest, updateApplication] = useOpenApiRequest(getApiRegistry().applicationsApi.update);
    const [deleteApplicationRequest, deleteApplication] = useOpenApiRequest(getApiRegistry().applicationsApi.delete);

    var createdDate = props.application.created ? props.application.created.toLocaleDateString("fi-FI") : '';
    var startDate = props.application.startDate ? props.application.startDate.toLocaleDateString("fi-FI") : '';
    var endDate = props.application.endDate ? props.application.endDate.toLocaleDateString("fi-FI") : '';

    useEffect(() => {
        if (updateApplicationRequest.type === OpenApiRequestStatus.Pending || deleteApplicationRequest.type === OpenApiRequestStatus.Pending) {
            setApplicationRequestPending(true);
        }
        if (updateApplicationRequest.type === OpenApiRequestStatus.Complete || deleteApplicationRequest.type === OpenApiRequestStatus.Complete) {
            setShowModal(false);
            setModalUpdateError(false);
            setApplicationRequestPending(false);
            props.onUpdate();
        } else if (updateApplicationRequest.type === OpenApiRequestStatus.NetworkError || updateApplicationRequest.type === OpenApiRequestStatus.ServerError ||
            deleteApplicationRequest.type === OpenApiRequestStatus.NetworkError || deleteApplicationRequest.type === OpenApiRequestStatus.ServerError) {
                setApplicationRequestPending(false);
                setModalUpdateError(true);
        }
        // eslint-disable-next-line
    }, [updateApplicationRequest, deleteApplicationRequest]);

    function handleClick(clickedModal: string) {
        if (clickedModal === 'Accept' || clickedModal === 'Decline') {
            updateApplication({ ...props.application, status: clickedModal === 'Accept' ? ApplicationStatus.ACCEPTED : ApplicationStatus.DECLINED });
        } else if (clickedModal === 'Delete') {
            deleteApplication(props.application.id);
        }
    }

    const handleClose = () => {
        setShowModal(false);
        setModalUpdateError(false);
    }

    return (
        <div>
            {!showFullItem &&
                <ItemWrapper>
                    <Column>
                        <MobileItemClosedIcon theme={theme} onClick={() => setShowFullItem(true)} />
                    </Column>
                    <HeaderColumn>
                        <ColumnItem>
                            {props.type === ViewType.SENT_APPLICATIONS ? t(sentApplicationsTableColumns[0].columnName) : t(receivedApplicationsTableColumns[0].columnName)}
                        </ColumnItem>
                    </HeaderColumn>
                    <Column>
                        <ColumnItem>{props.application.requesteeCustomerNumber}</ColumnItem>
                    </Column>
                </ItemWrapper>
            }
            {showFullItem &&
                <ItemWrapper>
                    <Column>
                        <MobileItemOpenedIcon theme={theme} onClick={() => setShowFullItem(false)} />
                    </Column>
                    <HeaderColumn>
                        {props.type === ViewType.SENT_APPLICATIONS && sentApplicationsTableColumns.map((row, index) =>
                            <ColumnItem key={index}>{t(row.columnName)}</ColumnItem>)}
                        {props.type === ViewType.RECEIVED_APPLICATIONS && receivedApplicationsTableColumns.map((row, index) =>
                            <ColumnItem key={index}>{t(row.columnName)}</ColumnItem>)}
                        {props.application.status === ApplicationStatus.PENDING &&
                            <ColumnItem>{t("functions")}</ColumnItem>}
                    </HeaderColumn>
                    <Column>
                        <ColumnItem>{props.application.requesteeCustomerNumber}</ColumnItem>
                        <ColumnItem>{props.type === ViewType.SENT_APPLICATIONS ? props.application.requesteeUsername : props.application.requestorUser?.username}</ColumnItem>
                        {props.type === ViewType.RECEIVED_APPLICATIONS && <ColumnItem>{props.application.requestorUser?.firstName + " " + props.application.requestorUser?.lastName}</ColumnItem>}
                        <ColumnItem>{startDate} - {endDate}</ColumnItem>
                        {props.type === ViewType.SENT_APPLICATIONS &&
                            <ColumnItem>{createdDate}</ColumnItem>}
                        <StatusColumn theme={{ status: (props.application.status).replace('_BY_ADMIN', '') }}>
                            {t(applicationStatusToString(props.application.status))}
                        </StatusColumn>
                        <IconColumnItem>
                            {props.type === ViewType.SENT_APPLICATIONS && props.application.status === ApplicationStatus.PENDING &&
                                <MobileItemDeleteIcon onClick={() => { setShowModal(true); setModalType('Delete'); }} />}
                            {props.type === ViewType.RECEIVED_APPLICATIONS && props.application.status === ApplicationStatus.PENDING && <>
                                <MobileItemAcceptIcon onClick={() => { setShowModal(true); setModalType('Accept'); }} />
                                <MobileItemDeclineIcon onClick={() => { setShowModal(true); setModalType('Decline'); }} /></>
                            }
                        </IconColumnItem>
                    </Column>
                </ItemWrapper>
            }
            <ApplicationModal 
                show={showModal} 
                modalType={modalType} 
                application={props.application}
                closeModal={handleClose} 
                handleClick={handleClick} 
                showError={modalUpdateError} 
                applicationRequestPending={applicationRequestPending} />
        </div>
    )
}

export default ApplicationMobileItem;